"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "refrigerator-lifespan-calculator",
  "cooking-temperature-converter",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function KitchenSpacePlannerPageClient() {
  const meta = TOOL_METADATA["kitchen-space-planner"]

  const [width, setWidth] = useState("36")
  const [height, setHeight] = useState("72")
  const [depth, setDepth] = useState("30")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const w = readQuantity(width, { min: 10, max: 120 })
    const h = readQuantity(height, { min: 20, max: 100 })
    const d = readQuantity(depth, { min: 10, max: 48 })
    if (w === null || h === null || d === null) return null

    // Manufacturer guidance: 2" side clearance, 1" top, 2" rear ventilation.
    const maxWidth = w - 4
    const maxHeight = h - 1
    const maxDepth = d - 2

    let recommendation: string
    if (maxWidth < 30) recommendation = "Compact or apartment-size refrigerator"
    else if (maxWidth < 33) recommendation = "Standard width (30–32 in)"
    else if (maxWidth < 36) recommendation = "Wide refrigerator or French door"
    else recommendation = "Any standard fridge will fit"

    return { maxWidth, maxHeight, maxDepth, recommendation }
  }, [width, height, depth])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="Will the fridge"
      heroTitleItalic="actually"
      heroTitlePost="fit?"
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~20 sec", "Manufacturer clearances"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: '2"', label: "Side clearance", note: "Each side" },
            { value: '1"', label: "Top clearance", note: "Above unit" },
            { value: '2"', label: "Rear ventilation", note: "Wall gap" },
            { value: '4"', label: "Door swing", note: "Hinge side" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "kitchen-space-planner")}
      cta={{
        title: "Found your dimensions? Now find the fridge.",
        body: "Filter by exact width, height, and depth. Our spec sheets include installed and delivery dimensions for every model.",
        buttonLabel: "Browse counter-depth fridges",
        buttonHref: "/collections/counter-depth-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Measure twice.
                <br />
                Order once.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Enter the cabinet opening or alcove dimensions in inches.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Opening width
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={10}
                      max={120}
                      step="0.125"
                      value={width}
                      onChange={(e) => setWidth(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">in</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Opening height
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={20}
                      max={100}
                      step="0.125"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">in</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Opening depth
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={10}
                      max={48}
                      step="0.125"
                      value={depth}
                      onChange={(e) => setDepth(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">in</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Standard kitchen alcoves: 36&quot; × 70&quot; × 30&quot;
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Maximum fridge dimensions
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(40px, 6vw, 64px)", letterSpacing: "-0.045em", lineHeight: 0.95 }}
                  >
                    {result?.maxWidth ?? "—"}&quot; × {result?.maxHeight ?? "—"}&quot; × {result?.maxDepth ?? "—"}&quot;
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.recommendation ?? "Enter dimensions to see what fits."}
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    {[
                      { l: "Width", v: result ? `${result.maxWidth}"` : "—" },
                      { l: "Height", v: result ? `${result.maxHeight}"` : "—" },
                      { l: "Depth", v: result ? `${result.maxDepth}"` : "—" },
                    ].map((s) => (
                      <div key={s.l} className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">{s.l}</div>
                        <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{s.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the fit"
          title="Counter-depth fridges that sit flush"
          description="24–27 inch depth — perfect for tight alcoves and modern kitchens."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Clearances come from <em className="italic text-[#ff9951]">manufacturers.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                We use the conservative end of installation guidance from major brands (LG, Whirlpool, Samsung, GE) so the unit operates correctly and the warranty stays valid.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Side clearance",
                  d: "Subtract 4 inches total (2 inches per side) from your width measurement to allow for door hinges and ventilation.",
                },
                {
                  n: "02",
                  t: "Top clearance",
                  d: "Subtract 1 inch from height for top ventilation. Built-in models have specific top-grille requirements.",
                },
                {
                  n: "03",
                  t: "Rear ventilation",
                  d: "Subtract 2 inches from depth for rear airflow. Skipping this causes the compressor to work harder and shortens lifespan.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door fridges for standard openings"
          description="36-inch wide, balanced fresh/frozen capacity — the most popular replacement style."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
